import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import ModeStatBox from './ModeStatBox';
import MapPerformanceTable from './MapPerformanceTable';
import HPMatchDropdown from './HPMatchDropdown';
import ModeMatchDropdown from './ModeMatchDropdown';
import SeasonFilter from './SeasonFilter';
import { getCurrentSeason, SEASONS } from '../data/seasons';
import { listSeasons } from '../utils/seasonApi';
import {
  MATCH_TYPE_CATEGORIES,
  getMatchTypeOptions,
  getMatchTypeRegions,
  getSeasonConfig,
} from '../utils/matchTypeCatalog';

function generateMapPerformance(matches, mode) {
  const mapStats = {};

  matches.forEach((match) => {
    const map = match.Map?.trim();
    if (!map) return;

    if (!mapStats[map]) {
      mapStats[map] = {
        map,
        wins: 0,
        losses: 0,
        pf: 0,
        pa: 0,
        attackWins: 0,
        attackRounds: 0,
        defenseWins: 0,
        defenseRounds: 0,
      };
    }

    const isWin = match.WinLose?.toUpperCase() === 'WIN';
    mapStats[map].wins += isWin ? 1 : 0;
    mapStats[map].losses += isWin ? 0 : 1;
    mapStats[map].pf += parseInt(match.PF || 0, 10);
    mapStats[map].pa += parseInt(match.PA || 0, 10);

    if (mode !== 'HP') {
      mapStats[map].attackWins += parseInt(match.OffWins || 0, 10);
      mapStats[map].attackRounds +=
        parseInt(match.OffWins || 0, 10) + parseInt(match.OffLosses || 0, 10);
      mapStats[map].defenseWins += parseInt(match.DefWins || 0, 10);
      mapStats[map].defenseRounds +=
        parseInt(match.DefWins || 0, 10) + parseInt(match.DefLosses || 0, 10);
    }
  });

  return Object.values(mapStats).map((entry) => {
    const totalGames = entry.wins + entry.losses;
    const avgPF = totalGames > 0 ? Math.round(entry.pf / totalGames) : 0;
    const avgPA = totalGames > 0 ? Math.round(entry.pa / totalGames) : 0;
    const avgDiff = avgPF - avgPA;

    const winRate = totalGames > 0 ? `${((entry.wins / totalGames) * 100).toFixed(1)}%` : '-';
    const atkWinRate =
      entry.attackRounds > 0
        ? `${((entry.attackWins / entry.attackRounds) * 100).toFixed(1)}%`
        : '-';
    const defWinRate =
      entry.defenseRounds > 0
        ? `${((entry.defenseWins / entry.defenseRounds) * 100).toFixed(1)}%`
        : '-';

    return {
      Map: entry.map,
      Wins: entry.wins,
      Losses: entry.losses,
      WinRate: winRate,
      PF: avgPF,
      PA: avgPA,
      Diff: avgDiff > 0 ? `+${avgDiff}` : `${avgDiff}`,
      AttackWinRate: mode === 'HP' ? '-' : atkWinRate,
      DefenseWinRate: mode === 'HP' ? '-' : defWinRate,
    };
  });
}

const sortByModeID = (rows) =>
  [...rows].sort((a, b) => {
    const aNum = parseInt(String(a?.ModeID || '').split('_').pop(), 10) || 0;
    const bNum = parseInt(String(b?.ModeID || '').split('_').pop(), 10) || 0;
    return bNum - aNum;
  });

const normalizeFilterValue = (value) => String(value || '').trim().toUpperCase();

const LegacyDashboardOverview = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [region, setRegion] = useState('');
  const [enemyTeam, setEnemyTeam] = useState('');
  const [selectedMatchCategory, setSelectedMatchCategory] = useState('');
  const [selectedMatchRegion, setSelectedMatchRegion] = useState('');
  const [selectedMatchTypes, setSelectedMatchTypes] = useState([]);
  const [matchTypeDropdownOpen, setMatchTypeDropdownOpen] = useState(false);
  const [vsTeams, setVsTeams] = useState([]);
  const [matchTypes, setMatchTypes] = useState([]);
  const [matchTypeCatalog, setMatchTypeCatalog] = useState([]);
  const [authUid, setAuthUid] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [teamLogo, setTeamLogo] = useState(null);
  const [allStats, setAllStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);

  const [recentHPMatchLimit, setRecentHPMatchLimit] = useState(10);
  const [recentSNDLimit, setRecentSNDLimit] = useState(10);
  const [recentCTLLimit, setRecentCTLLimit] = useState(10);
  const [selectedHPMap, setSelectedHPMap] = useState('');
  const [selectedSNDMap, setSelectedSNDMap] = useState('');
  const [selectedCTLMap, setSelectedCTLMap] = useState('');
  const [selectedSeason, setSelectedSeason] = useState(getCurrentSeason());
  const [seasonDocId, setSeasonDocId] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonTeamName, setSeasonTeamName] = useState('');
  const [seasonTeamLogo, setSeasonTeamLogo] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setAuthUid(null);
        setTeamId(null);
        setTeamLogo(null);
        setSeasons([]);
        setAllStats([]);
        setMatchTypeCatalog([]);
        return;
      }
      setAuthUid(user.uid);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authUid) return;
    let active = true;

    (async () => {
      const userRef = doc(db, `users/${authUid}`);
      const userSnap = await getDoc(userRef);
      if (!active || !userSnap.exists()) return;
      const userData = userSnap.data() || {};
      setTeamId(userData.teamId || null);
    })().catch((err) => {
      console.error('Failed to resolve dashboard team', err);
    });

    return () => {
      active = false;
    };
  }, [authUid]);

  useEffect(() => {
    if (!teamId) return;
    const currentSeason = getCurrentSeason();
    setSelectedSeason(currentSeason);
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    let active = true;

    (async () => {
      const [seasonRows, teamSnap] = await Promise.all([
        listSeasons(teamId),
        getDoc(doc(db, `teams/${teamId}`)),
      ]);

      if (!active) return;
      setSeasons(seasonRows);
      setTeamLogo(teamSnap.exists() ? (teamSnap.data()?.logo || null) : null);
    })().catch((err) => {
      console.error('Failed to load dashboard metadata', err);
    });

    return () => {
      active = false;
    };
  }, [teamId]);

  useEffect(() => {
    const row =
      seasons.find((s) => String(s.year || s.id) === String(selectedSeason)) ||
      seasons.find((s) => String(s.id) === String(selectedSeason)) ||
      seasons[0];
    setSeasonDocId(row ? String(row.id) : null);
    setSelectedMatchCategory('');
    setSelectedMatchRegion('');
    setSelectedMatchTypes([]);
    setMatchTypeDropdownOpen(false);
  }, [selectedSeason, seasons]);

  useEffect(() => {
    if (!teamId) return;
    let active = true;

    (async () => {
      const configSnap = await getDoc(doc(db, `teams/${teamId}/configurations/stats`));
      if (!active) return;

      if (!configSnap.exists()) {
        setVsTeams([]);
        setMatchTypes([]);
        setMatchTypeCatalog([]);
        setSeasonTeamName('');
        setSeasonTeamLogo(teamLogo || '');
        return;
      }

      const config = configSnap.data();
      const seasonConfig = getSeasonConfig(config, selectedSeason);

      const normalizedTeams = (seasonConfig.vsTeams || []).map((t) => ({
        name: t.name.trim().toUpperCase(),
        region: t.region.trim().toUpperCase()
      }));
      setVsTeams(normalizedTeams);
      setMatchTypes(seasonConfig.matchTypes || []);
      setMatchTypeCatalog(seasonConfig.matchTypeCatalog || []);
      setSeasonTeamName(seasonConfig.teamName || '');
      setSeasonTeamLogo(seasonConfig.teamLogo || teamLogo || '');
    })().catch((err) => {
      console.error('Failed to load dashboard config', err);
      if (!active) return;
      setVsTeams([]);
      setMatchTypes([]);
      setMatchTypeCatalog([]);
      setSeasonTeamName('');
      setSeasonTeamLogo(teamLogo || '');
    });

    return () => {
      active = false;
    };
  }, [teamId, selectedSeason, teamLogo]);

  useEffect(() => {
    if (!teamId) return;
    let active = true;

    (async () => {
      setLoadingStats(true);
      const baseRef = seasonDocId
        ? collection(db, `teams/${teamId}/seasons/${seasonDocId}/database/teamStats/modes`)
        : collection(db, `teams/${teamId}/database/teamStats/modes`);

      const filters = [];
      if (startDate) filters.push(where('Date', '>=', startDate));
      if (endDate) filters.push(where('Date', '<=', endDate));
      if (region) filters.push(where('EnemyRegion', '==', region.toUpperCase()));
      if (enemyTeam) filters.push(where('EnemyTeam', '==', enemyTeam.toUpperCase()));

      const matchQuery = filters.length ? query(baseRef, ...filters) : query(baseRef);
      const snapshot = await getDocs(matchQuery);
      if (!active) return;
      const selectedCategoryKey = normalizeFilterValue(selectedMatchCategory);
      const selectedRegionKey = normalizeFilterValue(selectedMatchRegion);
      const selectedTypeSet = new Set(selectedMatchTypes.map(normalizeFilterValue));
      const catalogById = new Map(
        matchTypeCatalog.map((entry) => [String(entry.id || ''), entry]).filter(([id]) => id)
      );
      const catalogByLabel = new Map(
        matchTypeCatalog
          .map((entry) => [normalizeFilterValue(entry.label), entry])
          .filter(([label]) => label)
      );
      const rows = snapshot.docs
        .map((d) => d.data())
        .filter((row) => {
          const catalogEntry =
            catalogById.get(String(row.MatchTypeId || '')) ||
            catalogByLabel.get(normalizeFilterValue(row.MatchType));
          const rowCategory = normalizeFilterValue(row.MatchCategory || catalogEntry?.category);
          const rowRegion = normalizeFilterValue(row.MatchTypeRegion || catalogEntry?.region);
          const rowType = normalizeFilterValue(row.MatchType || catalogEntry?.label);

          if (selectedCategoryKey && rowCategory !== selectedCategoryKey) return false;
          if (selectedRegionKey && rowRegion !== selectedRegionKey) return false;
          if (selectedTypeSet.size && !selectedTypeSet.has(rowType)) return false;
          return true;
        });
      setAllStats(rows);
    })().catch((err) => {
      console.error('Failed to load dashboard stats', err);
      if (!active) return;
      setAllStats([]);
    }).finally(() => {
      if (active) setLoadingStats(false);
    });

    return () => {
      active = false;
    };
  }, [
    teamId,
    seasonDocId,
    startDate,
    endDate,
    region,
    enemyTeam,
    selectedMatchCategory,
    selectedMatchRegion,
    selectedMatchTypes,
    matchTypeCatalog,
  ]);

  const topTeamsByRegion = {
    GARENA: ['ELEVATE', 'STALWART', 'ABSOLUTE ZERO', 'ABC ESPORTS', 'THANATOS'],
    CHINA: ['Q9', 'XROCK', 'WOLVES', 'SPG', 'AG'],
    INDIA: ['GODLIKE', 'S8UL', 'XLR8'],
    EUROPE: ['ANIMUS', 'EXCLUSIVE', 'ACE GAMERS'],
    JAPAN: ['DIAVOLOS', 'XCEED'],
    LATAM: ['GALORYS', 'KAIZEN', 'AMIGOS', 'TLE ESPORTS'],
    NORTH_AMERICA: ['NA FALLOFFS']
  };

  const hp = useMemo(() => allStats.filter((item) => item.Mode === 'HP'), [allStats]);
  const snd = useMemo(() => allStats.filter((item) => item.Mode === 'SND'), [allStats]);
  const ctl = useMemo(() => allStats.filter((item) => item.Mode === 'CTL'), [allStats]);

  const sortedHP = useMemo(() => sortByModeID(hp), [hp]);
  const sortedSND = useMemo(() => sortByModeID(snd), [snd]);
  const sortedCTL = useMemo(() => sortByModeID(ctl), [ctl]);

  const hpMapOptions = useMemo(() => [...new Set(hp.map((m) => m.Map))].sort(), [hp]);
  const sndMapOptions = useMemo(() => [...new Set(snd.map((m) => m.Map))].sort(), [snd]);
  const ctlMapOptions = useMemo(() => [...new Set(ctl.map((m) => m.Map))].sort(), [ctl]);

  const mapStatsHP = useMemo(() => generateMapPerformance(sortedHP, 'HP'), [sortedHP]);
  const mapStatsSND = useMemo(() => generateMapPerformance(sortedSND, 'SND'), [sortedSND]);
  const mapStatsCTL = useMemo(() => generateMapPerformance(sortedCTL, 'CTL'), [sortedCTL]);

  const recentHPMatches = useMemo(
    () => sortedHP.slice(0, recentHPMatchLimit),
    [sortedHP, recentHPMatchLimit]
  );
  const recentSNDMatches = useMemo(
    () => snd.slice(-recentSNDLimit).reverse(),
    [snd, recentSNDLimit]
  );
  const recentCTLMatches = useMemo(
    () => ctl.slice(-recentCTLLimit).reverse(),
    [ctl, recentCTLLimit]
  );

  const matchCategoryOptions = useMemo(() => {
    const availableCategories = new Set(
      matchTypeCatalog
        .filter((entry) => entry.active !== false)
        .map((entry) => normalizeFilterValue(entry.category))
    );
    return MATCH_TYPE_CATEGORIES.filter((option) => availableCategories.has(option.value));
  }, [matchTypeCatalog]);

  const matchRegionOptions = useMemo(
    () => getMatchTypeRegions(matchTypeCatalog, selectedMatchCategory),
    [matchTypeCatalog, selectedMatchCategory]
  );

  const matchTypeOptions = useMemo(() => {
    const uniqueTypes = new Map();
    getMatchTypeOptions(matchTypeCatalog, {
      category: selectedMatchCategory,
      region: selectedMatchRegion,
    }).forEach((entry) => {
      const label = String(entry.label || '').trim();
      const key = normalizeFilterValue(label);
      if (label && !uniqueTypes.has(key)) {
        uniqueTypes.set(key, label);
      }
    });
    if (!uniqueTypes.size) {
      (matchTypes || []).forEach((type) => {
        const label = String(type || '').trim();
        const key = normalizeFilterValue(label);
        if (label && !uniqueTypes.has(key)) {
          uniqueTypes.set(key, label);
        }
      });
    }
    return Array.from(uniqueTypes.values()).sort((a, b) => a.localeCompare(b));
  }, [matchTypeCatalog, matchTypes, selectedMatchCategory, selectedMatchRegion]);

  const selectedMatchTypeSet = useMemo(
    () => new Set(selectedMatchTypes.map(normalizeFilterValue)),
    [selectedMatchTypes]
  );

  const matchTypeDropdownLabel = selectedMatchTypes.length
    ? `${selectedMatchTypes.length} selected`
    : 'All';
  const filterItemClass = (isActive) => `dashboard-filter-item ${isActive ? 'is-active' : ''}`;

  const toggleMatchType = (type) => {
    const key = normalizeFilterValue(type);
    setSelectedMatchTypes((current) => {
      const currentSet = new Set(current.map(normalizeFilterValue));
      if (currentSet.has(key)) {
        return current.filter((item) => normalizeFilterValue(item) !== key);
      }
      return [...current, type];
    });
  };

  const handleMatchCategoryChange = (value) => {
    setSelectedMatchCategory(value);
    setSelectedMatchRegion('');
    setSelectedMatchTypes([]);
    setMatchTypeDropdownOpen(false);
  };

  const handleMatchRegionChange = (value) => {
    setSelectedMatchRegion(value);
    setSelectedMatchTypes([]);
    setMatchTypeDropdownOpen(false);
  };

  const calculateWinRate = (rows) => {
    const wins = rows.reduce((sum, row) => sum + (parseInt(row.Wins, 10) || 0), 0);
    const losses = rows.reduce((sum, row) => sum + (parseInt(row.Losses, 10) || 0), 0);
    const total = wins + losses;
    return total ? (wins / total) * 100 : 0;
  };

  return (
    <div className="dashboard-overview" aria-busy={loadingStats}>
      <div
        className="dashboard-summary"
      >
        <div className="dashboard-summary-grid">
          <div className="dashboard-team-card">
          {(seasonTeamLogo || teamLogo) && (
            <img
              className="dashboard-team-logo"
              src={seasonTeamLogo || teamLogo}
              alt={seasonTeamName || 'Team Logo'}
            />
          )}
          {seasonTeamName && (
            <div className="dashboard-team-meta">
              <p>Team</p>
              <h2>{seasonTeamName}</h2>
              <span>
                {SEASONS[selectedSeason]?.name || selectedSeason}
              </span>
            </div>
          )}
          </div>
          <ModeStatBox
            mode="All Modes"
            wins={[...mapStatsHP, ...mapStatsSND, ...mapStatsCTL].reduce((s, r) => s + (parseInt(r.Wins, 10) || 0), 0)}
            losses={[...mapStatsHP, ...mapStatsSND, ...mapStatsCTL].reduce((s, r) => s + (parseInt(r.Losses, 10) || 0), 0)}
            winRate={calculateWinRate([...mapStatsHP, ...mapStatsSND, ...mapStatsCTL])}
          />
          <ModeStatBox
            mode="Hardpoint"
            wins={mapStatsHP.reduce((s, r) => s + (parseInt(r.Wins, 10) || 0), 0)}
            losses={mapStatsHP.reduce((s, r) => s + (parseInt(r.Losses, 10) || 0), 0)}
            winRate={calculateWinRate(mapStatsHP)}
          />
          <ModeStatBox
            mode="Search & Destroy"
            wins={mapStatsSND.reduce((s, r) => s + (parseInt(r.Wins, 10) || 0), 0)}
            losses={mapStatsSND.reduce((s, r) => s + (parseInt(r.Losses, 10) || 0), 0)}
            winRate={calculateWinRate(mapStatsSND)}
          />
          <ModeStatBox
            mode="Control"
            wins={mapStatsCTL.reduce((s, r) => s + (parseInt(r.Wins, 10) || 0), 0)}
            losses={mapStatsCTL.reduce((s, r) => s + (parseInt(r.Losses, 10) || 0), 0)}
            winRate={calculateWinRate(mapStatsCTL)}
          />
        </div>
      </div>

          <div className="filter-panel">
            <div className="aligned-left-block">
              <div className="dashboard-panel-heading">
                <p>Filters</p>
                <h3>Filter Stats</h3>
              </div>
              <div className="filter-grid">
                <SeasonFilter
                  selectedSeason={selectedSeason}
                  onSeasonChange={setSelectedSeason}
                  startDate={startDate}
                  onStartDateChange={setStartDate}
                  endDate={endDate}
                  onEndDateChange={setEndDate}
                />

                <div className={filterItemClass(!!selectedMatchCategory)}>
                  <label>Match Category</label>
                  <select
                    value={selectedMatchCategory}
                    onChange={(e) => handleMatchCategoryChange(e.target.value)}
                  >
                    <option value="">All</option>
                    {matchCategoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={filterItemClass(!!selectedMatchRegion)}>
                  <label>Competition Region</label>
                  <select
                    value={selectedMatchRegion}
                    onChange={(e) => handleMatchRegionChange(e.target.value)}
                    disabled={!matchRegionOptions.length}
                  >
                    <option value="">All</option>
                    {matchRegionOptions.map((matchRegion) => (
                      <option key={matchRegion} value={matchRegion}>
                        {matchRegion}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={filterItemClass(selectedMatchTypes.length > 0)}>
                  <label>Competition</label>
                  <div className="dashboard-match-type-dropdown">
                    <button
                      type="button"
                      className="dashboard-match-type-trigger"
                      onClick={() => setMatchTypeDropdownOpen((open) => !open)}
                      aria-expanded={matchTypeDropdownOpen}
                    >
                      <span>{matchTypeDropdownLabel}</span>
                      <span aria-hidden="true">v</span>
                    </button>

                    {matchTypeDropdownOpen && (
                      <div className="dashboard-match-type-menu">
                        <div className="dashboard-match-type-actions">
                          <button
                            type="button"
                            onClick={() => setSelectedMatchTypes(matchTypeOptions)}
                            disabled={!matchTypeOptions.length}
                          >
                            Select all
                          </button>
                          <button type="button" onClick={() => setSelectedMatchTypes([])}>
                            Clear
                          </button>
                        </div>

                        <div className="dashboard-match-type-list">
                          {matchTypeOptions.length ? (
                            matchTypeOptions.map((type) => (
                              <label
                                key={type}
                                className={`dashboard-match-type-option ${selectedMatchTypeSet.has(normalizeFilterValue(type)) ? 'selected' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedMatchTypeSet.has(normalizeFilterValue(type))}
                                  onChange={() => toggleMatchType(type)}
                                />
                                <span>{type}</span>
                              </label>
                            ))
                          ) : (
                            <p>No match types configured</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={filterItemClass(!!region)}>
                  <label>Opponent Region</label>
                  <select
                    value={region}
                    onChange={(e) => {
                      const selectedRegion = e.target.value;
                      setRegion(selectedRegion);
                      if (selectedRegion === '') {
                        setEnemyTeam('');
                      }
                    }}
                  >
                    <option value="">All</option>
                    {Object.keys(topTeamsByRegion).map((regionKey) => (
                      <option key={regionKey} value={regionKey}>{regionKey}</option>
                    ))}
                  </select>
                </div>

                <div className={filterItemClass(!!enemyTeam)}>
                  <label>Opponent</label>
                  <select
                    value={enemyTeam}
                    onChange={(e) => setEnemyTeam(e.target.value)}
                    disabled={!region}
                    required
                  >
                    <option value="">Select Opponent Team</option>
                    {vsTeams
                      .filter((team) => team.region === region)
                      .map((team, i) => (
                        <option key={i} value={team.name}>
                          {team.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

      <div className="mode-section-block">
        <MapPerformanceTable title="Map Performance - HP" data={mapStatsHP} mode="HP" />
        <HPMatchDropdown
          matches={recentHPMatches}
          limit={recentHPMatchLimit}
          onLimitChange={setRecentHPMatchLimit}
          mapOptions={hpMapOptions}
          selectedMap={selectedHPMap}
          onMapChange={setSelectedHPMap}
        />
      </div>

      <div className="mode-section-block">
        <MapPerformanceTable title="Map Performance - SND" data={mapStatsSND} mode="SND" />
        <ModeMatchDropdown
          matches={recentSNDMatches}
          limit={recentSNDLimit}
          onLimitChange={setRecentSNDLimit}
          mapOptions={sndMapOptions}
          selectedMap={selectedSNDMap}
          onMapChange={setSelectedSNDMap}
          mode="SND"
        />
      </div>

      <div className="mode-section-block">
        <MapPerformanceTable title="Map Performance - CTL" data={mapStatsCTL} mode="CTL" />
        <ModeMatchDropdown
          matches={recentCTLMatches}
          limit={recentCTLLimit}
          onLimitChange={setRecentCTLLimit}
          mapOptions={ctlMapOptions}
          selectedMap={selectedCTLMap}
          onMapChange={setSelectedCTLMap}
          mode="CTL"
        />
      </div>
    </div>
  );
};

export default LegacyDashboardOverview;
